// src/utils/quickLessonLogWriter.js
//
// Pass 68 — the ONE canonical, idempotent, actor-authorized writer for a Quick Lesson Log. The lessonInstance
// is the authoritative delivered-service record (Pass 66); Quick Log is a fast writer ONTO it, never a second
// lesson identity and never a new collection. Key hardening:
//   • an EXISTING lesson (booking-linked or already saved) is updated with ONLY the delivery fields — a
//     merge write, so Pass-66 lineage (bookingId/bookingRequestId/requestLineId/requestSlotId/clientId/
//     organizationId/instructorId/instructorAuthUserId/origin/scheduledAt) is PRESERVED, never overwritten.
//   • clientId is never copied into studentId/clientUserId — works for a no-Auth customer (no account created).
//   • the canonical business instructorId is preserved; the Auth uid stays in instructorAuthUserId.
//   • transaction get-or-write on a caller-supplied deterministic id → double-tap / retry / concurrent = one
//     record, no duplicate. An empty log can never become `completed` delivery evidence.

import { runTransaction, doc, serverTimestamp } from 'firebase/firestore';
import { applyQuickLessonLogToLessonInstance, applyCloseoutToLessonInstance } from './quickLessonLog';
import { createLessonInstancePayload, normalizeLessonInstanceRecord } from './lessonInstancePersistence';
import { canOperateOrganization, isTeachingMembership } from './platformAccess';
import { getOrganizationMemberships } from './organizationContext';

const LESSONS = 'lessonInstances';
const str = (v) => String(v == null ? '' : v).trim();
const MAX_NOTE = 4000;
const fail = (code, msg) => { const e = new Error(msg); e.code = code; return e; };

export const QUICK_LOG_DENIED = 'You do not have permission to log this lesson.';
export const QUICK_LOG_EMPTY = 'Add a note, focus, or outcome before completing the lesson.';

// §61 minimum evidence: a completed log must carry real delivery content (not an untouched draft).
export function quickLogHasEvidence({ furthestStageReached = '', outcomeChoice = 'in_progress', quickNote = '', targetSkillId = '' } = {}) {
  const note = str(quickNote);
  const progressed = str(outcomeChoice) && outcomeChoice !== 'in_progress';
  const staged = str(furthestStageReached) && furthestStageReached !== 'review';
  return !!(note || progressed || staged || str(targetSkillId));
}

// §55 + Pass 84.2 authorization for an EXISTING lesson: an org operator (owner/admin/ops) OR the CURRENT canonical
// instructor. "Current instructor" is resolved via the caller's LIVE target-org teaching membership whose canonical
// BUSINESS userId equals the lesson's `instructorId` — NOT the lesson's stored `instructorAuthUserId` snapshot (which an
// account relink would make stale, locking the legitimate instructor out). A real actor Auth uid is still required.
// business instructorId ≠ Auth uid stays separate; the stored `instructorAuthUserId` is historical lineage only.
export function canWriteQuickLog(lesson = {}, ctx = {}) {
  // Authority is the LESSON's own organization (the resource being written) — NOT a caller-supplied ctx.organizationId,
  // so a cross-org caller cannot self-authorize by passing their own org. Fall back to ctx org only for a legacy no-org lesson.
  const org = str(lesson.organizationId) || str(ctx.organizationId);
  if (!org) return false;
  if (ctx.userData && canOperateOrganization(ctx.userData, org)) return true;
  const lessonInstructorId = str(lesson.instructorId);
  if (!lessonInstructorId || !str(ctx.actorAuthUserId)) return false;
  // The authenticated caller must hold a CURRENT teaching membership in the LESSON's org whose business userId IS the
  // lesson's canonical instructor. (ctx.userData is the authenticated user's own doc, so this binds to their real Auth id.)
  return getOrganizationMemberships(ctx.userData).some(
    (m) => str(m.organizationId) === org && isTeachingMembership(m) && str(m.userId) === lessonInstructorId,
  );
}

// Pass 70A/70B SECURITY — authorization for CREATING a standalone/manual lesson. The prior code authorized a
// CREATE by comparing the caller's uid to a caller-SUPPLIED instructorAuthUserId (always true → any signed-in
// user could create a lesson in any org). A create now requires REAL membership in the TARGET org: an operator
// (owner/admin/ops), or a teaching instructor member of that org creating THEIR OWN lesson.
//
// Pass 70B (§10/§11): "their own lesson" is now BOUND — the plain-instructor path requires a target-org
// teaching membership whose canonical BUSINESS id (`membership.userId`) equals the supplied actorInstructorId,
// AND a real actor Auth uid. So Instructor A (uid_A) cannot create a lesson attributed to Instructor B by
// passing actorInstructorId = inst_B: no membership of A carries userId == inst_B → denied. Operators keep the
// on-behalf path (they may legitimately create for another instructor, existing product behavior).
export function canCreateStandaloneLesson(ctx = {}) {
  const org = str(ctx.organizationId);
  if (!org || !ctx.userData) return false;
  if (canOperateOrganization(ctx.userData, org)) return true;
  // plain instructor: must present a real Auth identity AND a business instructor id...
  const actorInstructorId = str(ctx.actorInstructorId);
  const actorAuthUserId = str(ctx.actorAuthUserId);
  if (!actorInstructorId || !actorAuthUserId) return false;
  // ...that matches THEIR OWN canonical teaching membership (userId) in THIS org. Never another instructor's.
  return getOrganizationMemberships(ctx.userData).some(
    (m) => str(m.organizationId) === org && isTeachingMembership(m) && str(m.userId) === actorInstructorId
  );
}

const clampDelivery = (d = {}) => ({ ...d, quickNote: str(d.quickNote).slice(0, MAX_NOTE) });

// saveQuickLessonLog(db, { lessonInstanceId, delivery, status, seed?, userData, actorAuthUserId, actorInstructorId, organizationId })
//   • lessonInstanceId REQUIRED — deterministic (Pass-66 booking lesson id, or a per-draft standalone id the
//     caller generates ONCE and reuses) so every retry lands on the same doc.
//   • existing doc → merge delivery only (lineage preserved). missing doc → create standalone from `seed`.
export async function saveQuickLessonLog(db, { lessonInstanceId = '', delivery = {}, status = 'draft', seed = null, mode = 'quicklog', ...ctx } = {}) {
  const id = str(lessonInstanceId);
  if (!id) throw fail('QUICK_LOG_ID_REQUIRED', 'A lesson id is required.');
  const clean = clampDelivery(delivery);
  if (status === 'completed' && !quickLogHasEvidence(clean)) throw fail('QUICK_LOG_EMPTY', QUICK_LOG_EMPTY);
  const closeout = mode === 'closeout';

  let created = false;
  await runTransaction(db, async (tx) => {
    const ref = doc(db, LESSONS, id);
    const snap = await tx.get(ref);
    const exists = snap.exists && snap.exists();
    if (exists) {
      const current = normalizeLessonInstanceRecord({ id, ...snap.data() }, id);
      if (!canWriteQuickLog(current, ctx)) throw fail('QUICK_LOG_DENIED', QUICK_LOG_DENIED);
      if (closeout) {
        // Pass 84.1 — CLOSEOUT mode: ADDITIVE. Preserve ALL pre-existing structured progress. Write ONLY the human
        // closeout note (+ an EXPLICITLY chosen outcome, if any); `stages` are NEVER touched, so a rich Lesson Builder
        // lesson cannot be silently downgraded. `outcomeChoice` is applied only when the instructor explicitly edited it.
        const next = applyCloseoutToLessonInstance(current, {
          quickNote: clean.quickNote,
          outcomeChoice: clean.explicitOutcome ? clean.outcomeChoice : null,
          recommendedNextSkillId: str(clean.recommendedNextSkillId),
        });
        tx.set(ref, {
          lessonOutcome: next.lessonOutcome,
          status,
          loggedByUserId: str(ctx.actorAuthUserId),
          updatedAt: serverTimestamp(),
        }, { merge: true });
        return;
      }
      const next = applyQuickLessonLogToLessonInstance(current, clean);
      // MERGE delivery fields ONLY — never lineage/identity. bookingId/clientId/instructorId/org stay put.
      tx.set(ref, {
        stages: next.stages,
        lessonOutcome: next.lessonOutcome,
        ...(str(clean.sport) ? { sport: str(clean.sport) } : {}),
        ...(str(clean.targetSkillId) ? { targetSkillId: str(clean.targetSkillId) } : {}),
        status,
        loggedByUserId: str(ctx.actorAuthUserId),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      return;
    }
    // CREATE — standalone/manual lesson (no booking). Requires an explicit seed identifying org + instructor.
    if (!seed) throw fail('QUICK_LOG_NOT_FOUND', 'This lesson could not be found.');
    const base = {
      organizationId: str(seed.organizationId || ctx.organizationId),
      instructorId: str(seed.instructorId || ctx.actorInstructorId),   // canonical business id, NOT the Auth uid
      instructorAuthUserId: str(ctx.actorAuthUserId),
      origin: 'manual',
      // §12/§32 — never fabricate participant identity from clientId; carry only what the caller explicitly set.
      studentId: str(seed.studentId), studentName: str(seed.studentName), studentEmail: str(seed.studentEmail),
      clientId: str(seed.clientId),
      sport: str(clean.sport || seed.sport), targetSkillId: str(clean.targetSkillId || seed.targetSkillId),
    };
    if (!base.organizationId) throw fail('QUICK_LOG_ORG_REQUIRED', 'Organisation is required.');
    // §4 — authorize the CREATE against REAL org membership, never a caller-supplied identity field.
    if (!canCreateStandaloneLesson({ ...ctx, organizationId: base.organizationId })) throw fail('QUICK_LOG_DENIED', QUICK_LOG_DENIED);
    const payload = createLessonInstancePayload(applyQuickLessonLogToLessonInstance(base, clean), { status });
    tx.set(ref, { ...payload, createdBy: str(ctx.actorAuthUserId), createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    created = true;
  });
  return { lessonInstanceId: id, created, status };
}
